import React, { useState } from "react";
import { Form, Button, Modal, Checkbox, Message, Icon } from "semantic-ui-react";
import { determineSocialUnits } from "../../../../utils/dataAndFormulas/socialForms";
import { useLanguage } from "../../../../contexts";
import "./UpdateUnitsModal.scss";

export function UpdateUnitsModal(props) {
  const { show, onClose, onApply, currentUnits, registeredFields, isLoading } = props;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [selectedFields, setSelectedFields] = useState({});
  const [fieldUnits, setFieldUnits] = useState({});

  const allSocialFields = [
    { key: "days_worked", label: t("days_worked") },
    { key: "normal_hours_worked", label: t("normal_hours_worked") },
    { key: "fifty_percent_overtime", label: t("fifty_percent_overtime") },
    { key: "one_hundred_percent_overtime", label: t("one_hundred_percent_overtime") },
    { key: "female_production_workers", label: t("female_production_workers") },
    { key: "male_production_workers", label: t("male_production_workers") },
    { key: "other_gender_production_workers", label: t("other_gender_production_workers") },
    { key: "female_administrative_workers", label: t("female_administrative_workers") },
    { key: "male_administrative_workers", label: t("male_administrative_workers") },
    { key: "administrative_workers_other_genders", label: t("administrative_workers_other_genders") },
    { key: "female_temporary_workers", label: t("female_temporary_workers") },
    { key: "male_temporary_workers", label: t("male_temporary_workers") },
    { key: "temporary_workers_other_genders", label: t("temporary_workers_other_genders") },
    { key: "female_workers_leadership_positions", label: t("female_workers_leadership_positions") },
    { key: "male_workers_leadership_positions", label: t("male_workers_leadership_positions") },
    { key: "workers_under_cct", label: t("workers_under_cct") },
    { key: "work_accidents_with_sick_days", label: t("work_accidents_with_sick_days") },
    { key: "in_itine_accidents", label: t("in_itine_accidents") },
    { key: "first_aid_without_sick_days", label: t("first_aid_without_sick_days") },
    { key: "hours_training_social_issues", label: t("hours_training_social_issues") },
    { key: "hours_training_hys", label: t("hours_training_hys") },
    { key: "hours_environmental_awareness_training", label: t("hours_environmental_awareness_training") },
    { key: "volunteer_hours", label: t("volunteer_hours") },
    { key: "complaints_or_suggestions_received", label: t("complaints_or_suggestions_received") },
  ];

  const socialFields =
    registeredFields && registeredFields.length > 0
      ? registeredFields
          .filter((field) => field.active !== false)
          .map((field) => ({
            key: field.name,
            label: t(field.name),
            code: field.code,
          }))
      : allSocialFields;

  const getUnitOptions = (fieldKey) => {
    const units = determineSocialUnits(fieldKey);
    return units.map((u) => ({ key: u.key, text: u.name, value: u.value }));
  };

  const handleFieldToggle = (fieldKey, checked) => {
    setSelectedFields(prev => ({ ...prev, [fieldKey]: checked }));
    if (checked && !fieldUnits[fieldKey]) {
      setFieldUnits(prev => ({ ...prev, [fieldKey]: currentUnits?.[fieldKey] || "" }));
    }
  };

  const handleUnitChange = (fieldKey, unit) => {
    setFieldUnits(prev => ({ ...prev, [fieldKey]: unit }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    const allUnits = {};
    socialFields.forEach(field => {
      allSelected[field.key] = true;
      allUnits[field.key] = currentUnits?.[field.key] || "";
    });
    setSelectedFields(allSelected);
    setFieldUnits(allUnits);
  };

  const handleDeselectAll = () => {
    setSelectedFields({});
  };

  const handleApply = () => {
    const unitsToApply = {};
    Object.keys(selectedFields).forEach(key => {
      if (selectedFields[key] && fieldUnits[key]) {
        unitsToApply[key] = fieldUnits[key];
      }
    });
    if (Object.keys(unitsToApply).length === 0) return;
    onApply(unitsToApply);
  };

  const selectedCount = Object.values(selectedFields).filter(Boolean).length;

  return (
    <Modal open={show} onClose={onClose} size="large" className="update-units-modal">
      {/* <Modal.Header>
        <Icon name="edit" />
        {t("update_units_in_forms") || "Modificar unidades en formularios existentes"}
      </Modal.Header> */}
      <Modal.Content scrolling>
        <div className="update-units-modal__title">{t("update_units_in_forms") || "Modificar unidades"}</div>
        {/* <Message info>
          <Message.Header>{t("select_fields_to_update") || "Selecciona los campos a modificar"}</Message.Header>
          <p>
            {t("update_units_description") ||
              "Selecciona los campos y las unidades que deseas aplicar a todos los formularios existentes del año."}
          </p>
        </Message> */}

        <div style={{ marginBottom: "15px" }}>
          <Button size="small" className="btn-select-all" onClick={handleSelectAll}>
            <Icon name="check square" /> {t("select_all") || "Seleccionar todos"}
          </Button>
          <Button size="small" className="btn-deselect-all" onClick={handleDeselectAll}>
            <Icon name="square outline" /> {t("deselect_all") || "Deseleccionar todos"}
          </Button>
          <span style={{ marginLeft: "15px", color: "#666" }}>
            {selectedCount} {t("fields_selected") || "campos seleccionados"}
          </span>
        </div>

        <Form>
          <div className="update-units-grid">
            {socialFields.map((field) => (
              <div key={field.key} className="update-unit-row">
                <Checkbox
                  checked={selectedFields[field.key] || false}
                  onChange={(_, data) => handleFieldToggle(field.key, data.checked)}
                  label={field.label}
                  className="field-checkbox"
                />
                {selectedFields[field.key] && (
                  <Form.Dropdown
                    placeholder={t("select_unit")}
                    options={getUnitOptions(field.key)}
                    selection
                    search
                    compact
                    value={fieldUnits[field.key] || ""}
                    onChange={(_, data) => handleUnitChange(field.key, data.value)}
                    className="unit-dropdown"
                  />
                )}
              </div>
            ))}
          </div>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          primary
          onClick={handleApply}
          disabled={selectedCount === 0 || isLoading}
          loading={isLoading}
        >
          <Icon name="save" />
          {t("apply_to_forms") || `Aplicar a formularios (${selectedCount})`}
        </Button>
        <Button secondary onClick={onClose} disabled={isLoading}>
          {t("cancel")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
